import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { backendConfig } from '../config/backendConfig';
import CustomModal from '../components/CustomModal';

const backendBase = backendConfig.baseUrl;

export default function EditBookView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  useEffect(() => {
    async function fetchStory() {
      setLoading(true);
      try {
        const res = await fetch(`${backendBase}/api/stories/${id}`);
        if (!res.ok) throw new Error('Failed to fetch story');
        const data = await res.json();
        setStory(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchStory();
  }, [id]);

  const handleFieldChange = (field, value) => {
    setStory(prev => ({ ...prev, [field]: value }));
  };

  const handleSceneChange = (idx, field, value) => {
    setStory(prev => {
      const scenes = [...(prev.scenes || [])];
      scenes[idx] = { ...scenes[idx], [field]: value };
      return { ...prev, scenes };
    });
  };

  const handleAddScene = () => {
    setStory(prev => ({
      ...prev,
      scenes: [...(prev.scenes || []), { title: '', description: '', imageUrl: '' }]
    }));
  };

  const handleRemoveScene = idx => {
    setStory(prev => {
      const scenes = [...(prev.scenes || [])];
      scenes.splice(idx, 1);
      return { ...prev, scenes };
    });
  };

  const handleSave = async () => {
    try {
      const res = await fetch(`${backendBase}/api/stories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(story),
      });
      if (!res.ok) throw new Error('Failed to save story');
      
      setModal({
        isOpen: true,
        title: "📚 Book Saved Successfully!",
        message: "Wonderful! Your book has been updated and saved! ✨\n\nYour story is getting better and better! 🎨",
        type: 'save'
      });
      
      // Navigate after modal is closed
      setTimeout(() => {
        navigate(`/view-book/${id}`);
      }, 3000);
    } catch (err) {
      setModal({
        isOpen: true,
        title: "Oops! Something went wrong",
        message: "Don't worry, little author! Let's try saving your book again. Sometimes magic takes a moment to work! ✨",
        type: 'error'
      });
    }
  };

  if (loading) return <div className="p-8 text-amber-700">Loading...</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;
  if (!story) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 p-8 font-sans">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-amber-700">Edit Book</h1>
          <button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-6 rounded-xl shadow">Save</button>
        </div>
        <div className="bg-white/90 border-2 border-amber-200 rounded-2xl shadow-lg p-6">
          <input type="text" value={story.title} onChange={e => handleFieldChange('title', e.target.value)} className="text-2xl font-bold w-full mb-2 px-2 py-1 border-b-2 border-amber-200 bg-transparent focus:outline-none focus:border-amber-500" placeholder="Book Title" />
          <input type="text" value={story.author} onChange={e => handleFieldChange('author', e.target.value)} className="text-lg w-full mb-2 px-2 py-1 border-b-2 border-amber-100 bg-transparent focus:outline-none focus:border-amber-400" placeholder="Author" />
          <textarea value={story.brief || ''} onChange={e => handleFieldChange('brief', e.target.value)} className="w-full min-h-[60px] p-2 rounded-lg border-2 border-amber-100 focus:border-amber-400 focus:outline-none bg-amber-50/50 text-amber-900 mb-2" placeholder="Book Brief" />
          <textarea value={story.about || ''} onChange={e => handleFieldChange('about', e.target.value)} className="w-full min-h-[40px] p-2 rounded-lg border-2 border-amber-100 focus:border-amber-400 focus:outline-none bg-amber-50/50 text-amber-900 mb-2" placeholder="About the Author" />
          <div className="mb-4">
            <label className="block font-semibold text-amber-700 mb-2">Scenes</label>
            {(story.scenes || []).map((scene, idx) => (
              <div key={idx} className="mb-4 p-3 border rounded-xl bg-amber-50">
                <input type="text" value={scene.title} onChange={e => handleSceneChange(idx, 'title', e.target.value)} className="font-bold w-full mb-1 px-2 py-1 border-b-2 border-amber-100 bg-transparent focus:outline-none focus:border-amber-400" placeholder={`Scene ${idx + 1} Title`} />
                <textarea value={scene.description} onChange={e => handleSceneChange(idx, 'description', e.target.value)} className="w-full min-h-[40px] p-2 rounded-lg border-2 border-amber-100 focus:border-amber-400 focus:outline-none bg-amber-50/50 text-amber-900 mb-1" placeholder="Scene Description" />
                <input type="text" value={scene.imageUrl} onChange={e => handleSceneChange(idx, 'imageUrl', e.target.value)} className="w-full px-2 py-1 border-b-2 border-amber-100 bg-transparent focus:outline-none focus:border-amber-400" placeholder="Artwork URL" />
                <button onClick={() => handleRemoveScene(idx)} className="mt-2 text-xs text-red-500 hover:underline">Remove Scene</button>
              </div>
            ))}
            <button onClick={handleAddScene} className="bg-amber-300 hover:bg-amber-400 text-white font-semibold py-1 px-4 rounded-xl shadow">Add Scene</button>
          </div>
        </div>
      </div>

      {/* Custom Modal */}
      <CustomModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false, title: '', message: '', type: 'info' })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </div>
  );
} 